import { motion } from "framer-motion";
import { ChevronLeft, Lock, Shield, Eye, Database, Globe, Bell, Fingerprint, FileText, Mail, Chrome } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import SeoHelmet from "@/components/SeoHelmet";

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-[#030303] text-white selection:bg-white/10 selection:text-white">
      <SeoHelmet
        title="Privacy Policy"
        description="Read the Alvaru privacy policy. Learn how we protect your CV data, our AI processing standards, and your rights under GDPR and CCPA."
        canonical="https://www.alvaru.com/privacy"
      />
      {/* Background decoration */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-10%] w-[40%] h-[40%] bg-blue-500/5 blur-[120px] rounded-full" />
        <div className="absolute bottom-[-10%] left-[-10%] w-[40%] h-[40%] bg-purple-500/5 blur-[120px] rounded-full" />
      </div>

      <div className="relative max-w-4xl mx-auto px-6 pt-12 pb-24">
        {/* Navigation */}
        <motion.div 
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="mb-12"
        >
          <Link to="/">
            <Button variant="ghost" className="text-white/60 hover:text-white hover:bg-white/5 group pl-0">
              <ChevronLeft className="mr-2 h-4 w-4 transition-transform group-hover:-translate-x-1" />
              Back to landing page
            </Button>
          </Link>
        </motion.div>

        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-16"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-white/60 text-xs font-medium mb-6">
            <Lock className="w-3 h-3" />
            <span>Data Protection</span>
          </div>
          <h1 className="text-5xl font-bold mb-6 tracking-tight text-white uppercase italic">Privacy Policy</h1>
          <p className="text-white/40 text-lg">
            Last updated: <span className="text-white/60">April 4, 2026</span>
          </p>
        </motion.div>

        {/* Content */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="space-y-12"
        >
          {/* Section 1 */}
          <section className="group">
            <div className="flex items-start gap-4 mb-4">
              <div className="mt-1 p-2 rounded-lg bg-white/5 border border-white/10 group-hover:border-white/20 transition-colors">
                <Database className="w-5 h-5 text-white/60" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold mb-3">1. What we collect</h2>
                <p className="text-white/50 leading-relaxed text-lg">
                  When you sign in with Google, we receive your name, email address, and profile picture. We also store the CV documents you upload and the job descriptions you submit for tailoring.
                </p>
              </div>
            </div>
          </section>

          {/* Chrome Extension Data Collection */}
          <section className="group border-t border-white/5 pt-12">
            <div className="flex items-start gap-4 mb-4">
              <div className="mt-1 p-2 rounded-lg bg-white/5 border border-white/10 group-hover:border-white/20 transition-colors">
                <Chrome className="w-5 h-5 text-white/60" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold mb-3">Chrome Extension Data Collection</h2>
                <p className="text-white/50 leading-relaxed text-lg mb-4">
                  When you install and use the Alvaru Chrome Extension, the extension:
                </p>
                <ul className="space-y-3 text-white/50 text-lg">
                  <li className="flex gap-3"><span className="text-white/20 mt-1.5">•</span> <strong>Reads job posting data</strong> from supported websites (LinkedIn, Indeed, Reed, Glassdoor, Totaljobs, CV-Library) including job titles, company names, and job descriptions.</li>
                  <li className="flex gap-3"><span className="text-white/20 mt-1.5">•</span> <strong>Transmits your uploaded CV</strong> and the job description to our servers and AI provider (DeepSeek) solely for the purpose of generating a tailored CV.</li>
                  <li className="flex gap-3"><span className="text-white/20 mt-1.5">•</span> <strong>Does not read, collect, or transmit</strong> any other browsing activity, keystrokes, passwords, or personal data from other websites you visit.</li>
                  <li className="flex gap-3"><span className="text-white/20 mt-1.5">•</span> <strong>Does not operate</strong> on non-supported sites (e.g., banking, email, social media other than job pages).</li>
                </ul>
                <p className="mt-6 text-white/50 leading-relaxed text-lg">
                  The extension only activates on job listing pages of supported job boards and requires explicit user action (clicking the Alvaru button) to process any data. All CV tailoring and storage is handled through your Alvaru account as described above.
                </p>
              </div>
            </div>
          </section>

          {/* Section 2 */}
          <section className="group">
            <div className="flex items-start gap-4 mb-4">
              <div className="mt-1 p-2 rounded-lg bg-white/5 border border-white/10 group-hover:border-white/20 transition-colors">
                <Eye className="w-5 h-5 text-white/60" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold mb-3">2. How we use your data</h2>
                <ul className="space-y-3 text-white/50 text-lg">
                  <li className="flex gap-3"><span className="text-white/20 mt-1.5">•</span> To authenticate your account and provide access to the service.</li>
                  <li className="flex gap-3"><span className="text-white/20 mt-1.5">•</span> To generate tailored CVs and cover letters (Starter and Pro plans) based on your uploaded resume and job descriptions.</li>
                  <li className="flex gap-3"><span className="text-white/20 mt-1.5">•</span> To store your generated CVs and cover letters so you can access and download them later.</li>
                  <li className="flex gap-3"><span className="text-white/20 mt-1.5">•</span> To track your weekly generation count and enforce usage limits (Free, Starter, and Pro plans).</li>
                  <li className="flex gap-3"><span className="text-white/20 mt-1.5">•</span> To improve and develop the service, including analyzing usage patterns and generating aggregated, anonymized insights.</li>
                  <li className="flex gap-3"><span className="text-white/20 mt-1.5">•</span> To communicate with you about your account, service updates, or changes to these policies.</li>
                  <li className="flex gap-3"><span className="text-white/20 mt-1.5">•</span> To process payments via Stripe (your email and payment details are shared with Stripe for billing purposes).</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 3 */}
          <section className="group border-t border-white/5 pt-12">
            <div className="flex items-start gap-4 mb-4">
              <div className="mt-1 p-2 rounded-lg bg-white/5 border border-white/10 group-hover:border-white/20 transition-colors">
                <Shield className="w-5 h-5 text-white/60" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold mb-3">3. Legal basis for processing</h2>
                <p className="text-white/50 leading-relaxed mb-4 text-lg">We process your personal data under the following legal bases:</p>
                <div className="grid gap-4">
                  <div className="p-4 rounded-xl bg-white/5 border border-white/5">
                    <p className="text-white/80 font-medium mb-1">Contractual necessity</p>
                    <p className="text-sm text-white/40 leading-relaxed">Processing your CV data and account information is necessary to provide the service you signed up for.</p>
                  </div>
                  <div className="p-4 rounded-xl bg-white/5 border border-white/5">
                    <p className="text-white/80 font-medium mb-1">Legitimate interest</p>
                    <p className="text-sm text-white/40 leading-relaxed">We use your data to improve our service, develop new features, generate aggregated analytics, and ensure platform security.</p>
                  </div>
                  <div className="p-4 rounded-xl bg-white/5 border border-white/5">
                    <p className="text-white/80 font-medium mb-1">Consent</p>
                    <p className="text-sm text-white/40 leading-relaxed">By creating an account and uploading content, you consent to the collection and processing of your data as described in this policy.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 4 */}
          <section className="group border-t border-white/5 pt-12 text-lg">
            <div className="flex items-start gap-4 mb-4">
              <div className="mt-1 p-2 rounded-lg bg-white/5 border border-white/10 group-hover:border-white/20 transition-colors">
                <Sparkles className="w-5 h-5 text-white/60" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold mb-3">4. AI processing</h2>
                <p className="text-white/50 leading-relaxed">
                  Your CV text and job descriptions are sent to DeepSeek's API to generate tailored CVs and cover letters. DeepSeek does not use API inputs to train its models. See DeepSeek's privacy policy for details.
                </p>
              </div>
            </div>
          </section>

          {/* Section 5 */}
          <section className="group border-t border-white/5 pt-12">
            <div className="flex items-start gap-4 mb-4">
              <div className="mt-1 p-2 rounded-lg bg-white/5 border border-white/10 group-hover:border-white/20 transition-colors">
                <Lock className="w-5 h-5 text-white/60" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold mb-3">5. Data storage and security</h2>
                <p className="text-white/50 leading-relaxed text-lg">
                  Your data is stored on our servers located in the United Kingdom. Uploaded PDFs and generated CVs are stored as files on disk. Data is stored in its functional form without additional encryption at rest, as we prioritize service reliability and performance. We implement reasonable administrative and technical safeguards to protect your data, but no method of storage or transmission is 100% secure. We do not sell or rent your personal data. We may share data with third parties as required to provide the service (DeepSeek for AI processing, Stripe for payment processing) or as required by law.
                </p>
              </div>
            </div>
          </section>

          {/* Section 6 */}
          <section className="group border-t border-white/5 pt-12">
            <div className="flex items-start gap-4 mb-4">
              <div className="mt-1 p-2 rounded-lg bg-white/5 border border-white/10 group-hover:border-white/20 transition-colors">
                <Globe className="w-5 h-5 text-white/60" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold mb-3">6. International data transfers</h2>
                <p className="text-white/50 leading-relaxed text-lg">
                  Alvaru is operated from the United Kingdom. If you access the service from outside the United Kingdom, your data will be transferred to and processed in the United Kingdom and the United States (for third-party services such as DeepSeek and Stripe). By using Alvaru, you explicitly consent to this transfer. These countries may not offer the same level of data protection as your home jurisdiction. We rely on your consent and contractual necessity as the legal basis for these transfers.
                </p>
              </div>
            </div>
          </section>

          {/* Section 7 */}
          <section className="group border-t border-white/5 pt-12">
            <div className="flex items-start gap-4 mb-4">
              <div className="mt-1 p-2 rounded-lg bg-white/5 border border-white/10 group-hover:border-white/20 transition-colors">
                <Bell className="w-5 h-5 text-white/60" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold mb-3">7. Third-party services</h2>
                <p className="text-white/50 leading-relaxed mb-4 text-lg">We share your data with the following third-party services as necessary to operate Alvaru:</p>
                <ul className="space-y-4">
                  <li className="flex items-center gap-4 p-4 rounded-xl bg-white/5 border border-white/5">
                    <div className="w-8 h-8 rounded bg-white font-bold text-black flex items-center justify-center text-xs italic">D</div>
                    <div>
                      <p className="font-medium">DeepSeek (AI API)</p>
                      <p className="text-sm text-white/40">AI processing for CV tailoring.</p>
                    </div>
                  </li>
                  <li className="flex items-center gap-4 p-4 rounded-xl bg-white/5 border border-white/5">
                    <div className="w-8 h-8 rounded bg-[#635BFF] font-bold text-white flex items-center justify-center text-[10px]">STRIPE</div>
                    <div>
                      <p className="font-medium">Stripe</p>
                      <p className="text-sm text-white/40">Secure subscription billing and payment processing.</p>
                    </div>
                  </li>
                  <li className="flex items-center gap-4 p-4 rounded-xl bg-white/5 border border-white/5">
                    <div className="w-8 h-8 rounded bg-white flex items-center justify-center text-[10px] text-blue-500 font-bold">G</div>
                    <div>
                      <p className="font-medium">Google (OAuth)</p>
                      <p className="text-sm text-white/40">Identity authentication and basic profile access.</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 8-10 */}
          <div className="grid md:grid-cols-2 gap-12 border-t border-white/5 pt-12">
            <section>
              <h2 className="text-2xl font-semibold mb-3">8. Data retention</h2>
              <p className="text-white/50 leading-relaxed text-lg">Your data is retained as long as your account is active. You can delete individual generated CVs and cover letters at any time from the dashboard. If you want your account and all associated data deleted, contact us.</p>
            </section>
            <section>
              <h2 className="text-2xl font-semibold mb-3">9. Data breach notification</h2>
              <p className="text-white/50 leading-relaxed text-lg">We will notify affected users via email within 72 hours of becoming aware of a breach, where feasible.</p>
            </section>
            <section className="md:col-span-2">
              <h2 className="text-2xl font-semibold mb-3 flex items-center gap-2">
                <Fingerprint className="w-6 h-6 text-white/60" />
                10. Cookies
              </h2>
              <p className="text-white/50 leading-relaxed text-lg">We use a session cookie strictly for authentication. We do not use tracking cookies, advertising cookies, or third-party analytics. Because we only use essential cookies, no cookie consent banner is necessary.</p>
            </section>
          </div>

          {/* Section 11 */}
          <section className="group border-t border-white/5 pt-12">
            <div className="flex items-start gap-4 mb-4">
              <div className="mt-1 p-2 rounded-lg bg-white/5 border border-white/10 group-hover:border-white/20 transition-colors">
                <FileText className="w-5 h-5 text-white/60" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold mb-3">11. Your rights</h2>
                <div className="grid md:grid-cols-2 gap-6 text-lg">
                  <div className="space-y-2 text-white/50">
                    <p><strong className="text-white/80">Access:</strong> Request a copy of your personal data.</p>
                    <p><strong className="text-white/80">Correction:</strong> Request correction of inaccurate data.</p>
                    <p><strong className="text-white/80">Deletion:</strong> Request deletion of your data and account.</p>
                  </div>
                  <div className="space-y-2 text-white/50">
                    <p><strong className="text-white/80">Portability:</strong> Export your CVs as PDFs at any time.</p>
                    <p><strong className="text-white/80">Withdrawal:</strong> Withdraw consent by deleting your account.</p>
                    <p><strong className="text-white/80">Objection:</strong> Object to processing based on legitimate interest.</p>
                  </div>
                </div>
                
                <div className="mt-8 p-6 rounded-2xl bg-white/[0.02] border border-white/5 space-y-4">
                  <p className="text-sm text-white/40"><strong className="text-white/60">GDPR (EU/EEA):</strong> You may lodge a complaint with your local data protection authority.</p>
                  <p className="text-sm text-white/40"><strong className="text-white/60">CCPA (California):</strong> We do not sell your personal information. You have the right to know what we collect.</p>
                  <p className="text-sm text-white/40"><strong className="text-white/60">UK GDPR / DPA 2018:</strong> You have the right to access and challenge the accuracy of your personal information held by us.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 12 */}
          <section className="group border-t border-white/5 pt-12 mb-12">
            <div className="p-8 rounded-3xl bg-white/5 border border-white/10 flex flex-col md:flex-row items-center justify-between gap-8">
              <div>
                <h2 className="text-3xl font-bold mb-2 uppercase italic text-white leading-none">Questions?</h2>
                <p className="text-white/50">For privacy-related questions or data requests, we're here to help.</p>
              </div>
              <a href="mailto:info@alvaru.com">
                <Button size="lg" className="bg-white text-black hover:bg-white/90 gap-2 h-14 px-8 rounded-2xl font-bold">
                  <Mail className="w-5 h-5" />
                  info@alvaru.com
                </Button>
              </a>
            </div>
          </section>
        </motion.div>

        {/* Footer info */}
        <div className="text-center text-white/20 text-sm border-t border-white/5 pt-12 uppercase tracking-widest">
          &copy; 2026 Alvaru. All rights reserved.
        </div>
      </div>
    </div>
  );
};

// Simple Sparkles component for internal use
const Sparkles = ({ className }: { className?: string }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
  >
    <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
    <path d="M5 3v4" /><path d="M19 17v4" /><path d="M3 5h4" /><path d="M17 19h4" />
  </svg>
);

export default PrivacyPage;
